import { cloneElement, ComponentProps, ReactElement } from 'react';

import { BlockedReason } from '../../../../backend/src/types/types';
import { useUserContext } from '@/Contexts/User';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

type TooltipContentProps = ComponentProps<typeof TooltipContent>;

export function useCreditBlockState(): { isBlocked: boolean; message: string } {
  const { user } = useUserContext();
  const isBlocked = user?.isBlocked === true;
  const message = isBlocked
    ? shortMessage(user.blockedReason, user.blockedUntil !== null ? new Date(user.blockedUntil) : null)
    : '';
  return { isBlocked, message };
}

/**
 * Short, one-line message suited for a button tooltip.
 * `blockedUntil` is included so users know when the block clears.
 */
const shortMessage = (reason: BlockedReason | null, blockedUntil: Date | null): string => {
  const until = blockedUntil !== null ? ` until ${new Date(blockedUntil).toLocaleDateString('en-GB')}` : '';
  switch (reason) {
    case BlockedReason.FairUsageStreak:
      return `You've exceeded your credit limit for 3 consecutive months${until}.`;
    case BlockedReason.ShadowLimitExceeded:
      return `You've exceeded the hard usage limit for this period${until}.`;
    case BlockedReason.OrgLimitExceeded:
      return `Your organisation is over its credit limit${until}.`;
    default:
      return `You're blocked from credit-consuming actions${until}.`;
  }
};

interface Props {
  children: ReactElement<{ disabled?: boolean }>;
  active?: boolean;
  side?: TooltipContentProps['side'];
  sideOffset?: TooltipContentProps['sideOffset'];
}

/**
 * Wraps a button that invokes a credit-consuming backend endpoint. When the current user is
 * blocked AND `active` is true, forces `disabled` on the child and shows a tooltip with the
 * reason.
 */
export function CreditBlockTooltip({ children, active = true, side, sideOffset }: Props) {
  const { user } = useUserContext();

  if (!active || user?.isBlocked !== true) return children;

  const message = shortMessage(user.blockedReason, user.blockedUntil !== null ? new Date(user.blockedUntil) : null);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Wrapping span so hover/focus works even though the child is disabled. */}
        <span tabIndex={0}>{cloneElement(children, { disabled: true })}</span>
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={sideOffset}>
        {message}
      </TooltipContent>
    </Tooltip>
  );
}
